import { useEffect, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileDown, Printer, MessageSquare, Award, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const TERM_LABELS = { first: "First Term", second: "Second Term", third: "Third Term" };

const ReportCards = () => {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => ["super_admin", "director"].includes(r));

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [term, setTerm] = useState("first");
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [rc, setRc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cmt, setCmt] = useState({ class_teacher: "", head_teacher: "" });

  useEffect(() => {
    api.get("/academic-sessions").then(({ data }) => {
      setSessions(data);
      const cur = data.find((s) => s.is_current) || data[0];
      if (cur) setSessionId(cur.id);
    });
  }, []);

  const search = async () => {
    if (!q.trim()) { setCandidates([]); return; }
    try {
      const { data } = await api.get("/students", { params: { q: q.trim(), session_id: sessionId } });
      setCandidates(data.slice(0, 20));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async (studentId, enrollment) => {
    if (!sessionId) return toast.error("Pick a session first");
    setLoading(true); setRc(null);
    try {
      const { data } = await api.get(`/assessments/report-card/${studentId}`, { params: { session_id: sessionId, term, program_id: enrollment?.program_id, class_id: enrollment?.class_id, arm_id: enrollment?.arm_id } });
      setRc(data);
      setCmt({ class_teacher: data.comments?.class_teacher || "", head_teacher: data.comments?.head_teacher || "" });
    } catch (e) {
      if (e?.response?.status === 423) toast.error("Results not yet published for this student");
      else toast.error(formatApiError(e));
    }
    finally { setLoading(false); }
  };

  const pick = (s, enrollment) => { setSelected(s); setSelectedEnrollment(enrollment); setCandidates([]); setQ(""); load(s.id, enrollment); };

  const saveComments = async () => {
    if (!rc || !selected) return;
    try {
      await api.post("/assessments/comments", {
        student_id: selected.id, class_id: rc.class.id, arm_id: rc.class.arm_id || null,
        session_id: sessionId, term,
        class_teacher_comment: cmt.class_teacher,
        head_teacher_comment: isAdmin ? cmt.head_teacher : undefined,
      });
      toast.success("Comment saved");
      await load(selected.id, selectedEnrollment);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = () => {
    if (!selected) return;
    const url = `${API_BASE}/assessments/report-card/${selected.id}/pdf?session_id=${sessionId}&term=${term}`;
    // fetch with auth header, then blob-download
    api.get(`/assessments/report-card/${selected.id}/pdf`, { params: { session_id: sessionId, term, program_id: selectedEnrollment?.program_id, class_id: selectedEnrollment?.class_id, arm_id: selectedEnrollment?.arm_id }, responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ReportCard_${selected.admission_no}_${term}.pdf`;
        document.body.appendChild(link); link.click(); link.remove();
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="report-cards-page">
      <PageHeader
        overline="Assessment"
        title="Report Cards"
        description="Look up any student and generate their terminal report card. Add class-teacher and head-teacher comments before printing/downloading."
        actions={
          <>
            <Select value={sessionId} onValueChange={(value) => { setSessionId(value); setSelected(null); setSelectedEnrollment(null); setRc(null); }}>
              <SelectTrigger className="h-10 w-44 bg-white" data-testid="rc-session"><SelectValue placeholder="Session" /></SelectTrigger>
              <SelectContent>{sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={term} onValueChange={setTerm}>
              <SelectTrigger className="h-10 w-36 bg-white" data-testid="rc-term"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First Term</SelectItem>
                <SelectItem value="second">Second Term</SelectItem>
                <SelectItem value="third">Third Term</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
      />

      {/* Search bar */}
      <Card className="border-[#E2E8F0] p-3 mb-4">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search student by name or admission #…" className="pl-9 h-10 bg-white" data-testid="rc-search-input" />
          </div>
          <Button onClick={search} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="rc-search-btn">Search</Button>
          {selected && rc?.status === "published" && (
            <span className="ml-auto text-xs px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" data-testid="rc-published"><Lock className="w-3 h-3" /> Published</span>
          )}
        </div>
        {candidates.length > 0 && (
          <div className="mt-2 border-t border-[#E2E8F0] pt-2 space-y-1 max-h-64 overflow-y-auto">
            {candidates.flatMap((s) => (s.programs || []).filter((enrollment) => enrollment.session_id === sessionId).map((enrollment) => (
              <button key={`${s.id}-${enrollment.enrollment_id}`} onClick={() => pick(s, enrollment)} className="w-full text-left px-2 py-1.5 rounded hover:bg-[#9DCD3F]/10 flex gap-3 items-center" data-testid={`rc-candidate-${s.id}-${enrollment.enrollment_id}`}>
                {s.photo ? <img src={s.photo} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-[10px]">{(s.first_name?.[0] || "?").toUpperCase()}</span>}
                <span className="text-sm text-[#1B2C5A] flex-1">{s.full_name}</span>
                <span className="text-xs text-[#475569]">{enrollment.program_name} · {enrollment.class_name}{enrollment.arm_name ? ` ${enrollment.arm_name}` : ""}</span>
                <span className="text-xs font-mono text-[#475569]">{s.admission_no}</span>
              </button>
            ))) }
          </div>
        )}
      </Card>

      {loading && <div className="text-center text-[#475569] py-8 inline-flex items-center gap-2 justify-center w-full"><Loader2 className="w-4 h-4 animate-spin" /> Building report card…</div>}

      {rc && (
        <Card className="border-[#E2E8F0] p-6" data-testid="rc-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="overline text-[#9DCD3F] inline-flex items-center gap-1.5"><Award className="w-3 h-3" /> {rc.term_label} · {rc.session?.name}</div>
              <h2 className="font-display text-2xl text-[#1B2C5A] mt-1">{rc.student.full_name}</h2>
              <p className="text-sm text-[#475569]">{rc.student.admission_no} · {rc.class.name}{rc.class.arm_name ? ` · ${rc.class.arm_name}` : ""} · {rc.class.program_name}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={downloadPdf} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A]" data-testid="rc-pdf-btn"><FileDown className="w-4 h-4 mr-1.5" /> PDF Report Card</Button>
              <Button variant="outline" onClick={() => window.print()} className="border-[#E2E8F0]" data-testid="rc-print-btn"><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
            </div>
          </div>

          {/* Subjects table */}
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="rc-subjects-table">
              <thead>
                <tr className="bg-[#1B2C5A] text-white">
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-center px-2 py-2">CA1</th>
                  <th className="text-center px-2 py-2">CA2</th>
                  <th className="text-center px-2 py-2">Exam</th>
                  <th className="text-center px-2 py-2">Att.</th>
                  <th className="text-center px-2 py-2">Total</th>
                  <th className="text-center px-2 py-2">Grade</th>
                  <th className="text-left px-3 py-2">Remark</th>
                  <th className="text-center px-2 py-2">Position</th>
                  <th className="text-center px-2 py-2">Class Avg</th>
                  <th className="text-center px-2 py-2">Highest</th>
                  <th className="text-center px-2 py-2">Lowest</th>
                </tr>
              </thead>
              <tbody>
                {rc.subjects.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-6 text-[#475569] italic">No subjects assigned to this class for this term.</td></tr>
                )}
                {rc.subjects.map((s, i) => (
                  <tr key={s.subject_id} className={i % 2 ? "bg-[#FDFBF7]" : "bg-white"} data-testid={`rc-sub-${s.subject_id}`}>
                    <td className="px-3 py-2 font-medium text-[#1B2C5A]">{s.subject_name}</td>
                    <td className="px-2 py-2 text-center">{s.ca1 || "—"}</td>
                    <td className="px-2 py-2 text-center">{s.ca2 || "—"}</td>
                    <td className="px-2 py-2 text-center">{s.exam || "—"}</td>
                    <td className="px-2 py-2 text-center">{s.attendance_marks}</td>
                    <td className="px-2 py-2 text-center font-semibold">{s.total}</td>
                    <td className="px-2 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-[#9DCD3F]/20 text-[#1B2C5A] font-medium">{s.grade}</span></td>
                    <td className="px-3 py-2 text-[#475569]">{s.remark}</td>
                    <td className="px-2 py-2 text-center">{s.position_ordinal || "—"}</td>
                    <td className="px-2 py-2 text-center text-[#475569]">{s.class_average ?? "—"}</td>
                    <td className="px-2 py-2 text-center text-[#475569]">{s.highest ?? "—"}</td>
                    <td className="px-2 py-2 text-center text-[#475569]">{s.lowest ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Overall + Attendance panels */}
          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div className="rounded-lg border border-[#9DCD3F]/40 bg-[#9DCD3F]/8 p-4" data-testid="rc-overall">
              <div className="overline text-[#9DCD3F]">Overall</div>
              <div className="grid grid-cols-2 gap-y-2 mt-2 text-sm">
                <div className="text-[#475569]">Total</div><div className="text-[#1B2C5A] font-semibold">{rc.overall.total}</div>
                <div className="text-[#475569]">Average %</div><div className="text-[#1B2C5A] font-semibold">{rc.overall.average_pct}%</div>
                <div className="text-[#475569]">Grade</div><div className="text-[#1B2C5A] font-semibold">{rc.overall.grade} — {rc.overall.remark}</div>
                <div className="text-[#475569]">Class Position</div><div className="text-[#1B2C5A] font-semibold">{rc.overall.position_ordinal || "—"} of {rc.overall.class_size || "—"}</div>
                <div className="text-[#475569]">Class Average</div><div className="text-[#1B2C5A] font-semibold">{rc.overall.class_average_pct ?? "—"}%</div>
              </div>
            </div>
            <div className="rounded-lg border border-[#E2E8F0] bg-white p-4">
              <div className="overline text-[#9DCD3F]">Attendance</div>
              <div className="grid grid-cols-2 gap-y-2 mt-2 text-sm">
                <div className="text-[#475569]">Attendance %</div><div className="text-[#1B2C5A] font-semibold">{rc.attendance.percentage}%</div>
                <div className="text-[#475569]">Attendance Marks</div><div className="text-[#1B2C5A] font-semibold">{rc.attendance.marks} / {rc.attendance.max}</div>
                <div className="text-[#475569]">Rule</div><div className="text-[#1B2C5A]">≥ {rc.attendance.threshold_pct}% earns full marks</div>
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="mt-5 space-y-3" data-testid="rc-comments">
            <div className="rounded-lg border border-[#E2E8F0] p-3 bg-[#FDFBF7]">
              <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-[#9DCD3F]" /><span className="text-sm font-medium text-[#1B2C5A]">Class Teacher&apos;s Comment</span></div>
              <Textarea rows={2} value={cmt.class_teacher} onChange={(e) => setCmt({ ...cmt, class_teacher: e.target.value })} disabled={rc.status === "published" && !isAdmin} data-testid="rc-comment-class" />
            </div>
            <div className="rounded-lg border border-[#E2E8F0] p-3 bg-[#FDFBF7]">
              <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-[#9DCD3F]" /><span className="text-sm font-medium text-[#1B2C5A]">Head Teacher&apos;s Comment {!isAdmin && <span className="text-xs italic text-[#475569] ml-2">— admin/director only</span>}</span></div>
              <Textarea rows={2} value={cmt.head_teacher} onChange={(e) => setCmt({ ...cmt, head_teacher: e.target.value })} disabled={!isAdmin} data-testid="rc-comment-head" />
            </div>
            <div className="flex justify-end">
              <Button onClick={saveComments} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="rc-save-comments">Save Comments</Button>
            </div>
          </div>
        </Card>
      )}

      {!rc && !loading && <EmptyState message="Search a student to view or print their report card." testId="rc-empty" />}
    </div>
  );
};

export default ReportCards;
